// src/pages/RolesPage.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';
import DataTable from '../components/DataTable';
import RoleForm from '../components/RoleForm';
import PermissionManager from '../components/PermissionManager';

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showPermissionManager, setShowPermissionManager] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_roles')
        .select('*')
        .order('level', { ascending: false });
      
      if (!error) setRoles(data || []);
    } catch (error) {
      console.error('Erreur chargement roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (roleData) => {
    try {
      // Générer le slug à partir du nom
      const slug = roleData.name.toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

      const { error } = await supabase
        .from('company_roles')
        .insert([{
          name: roleData.name,
          slug: slug,
          level: roleData.level || 0,
          permissions: roleData.permissions || [],
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      alert('✅ Rôle créé avec succès!');
      fetchRoles();
      setShowRoleForm(false);
    } catch (error) {
      console.error('Erreur création rôle:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleUpdateRole = async (roleData) => {
    try {
      const { error } = await supabase
        .from('company_roles')
        .update({
          name: roleData.name,
          level: roleData.level,
          permissions: roleData.permissions || [],
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRole.id);

      if (error) throw error;

      alert('✅ Rôle mis à jour!');
      fetchRoles();
      setShowRoleForm(false);
      setSelectedRole(null);
    } catch (error) {
      console.error('Erreur mise à jour rôle:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleUpdatePermissions = async (permissions) => {
    try {
      const { error } = await supabase
        .from('company_roles')
        .update({
          permissions: permissions,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRole.id);

      if (error) throw error;

      alert('✅ Permissions mises à jour!');
      fetchRoles();
      setShowPermissionManager(false);
      setSelectedRole(null);
    } catch (error) {
      console.error('Erreur mise à jour permissions:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rôle ?')) return;

    try {
      const { error } = await supabase
        .from('company_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;

      alert('✅ Rôle supprimé');
      fetchRoles();
    } catch (error) {
      console.error('Erreur suppression rôle:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const columns = [
    { 
      key: 'name', 
      label: 'Nom du rôle',
      render: (role) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
            <span className="text-blue-600 font-medium text-sm">
              {role.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-900">{role.name}</span>
            <span className="ml-2 text-xs text-gray-400">({role.slug})</span>
          </div>
        </div>
      )
    },
    { 
      key: 'level', 
      label: 'Niveau',
      render: (role) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          {role.level || 0}
        </span>
      )
    },
    {
      key: 'permissions',
      label: 'Permissions',
      render: (role) => {
        const perms = role.permissions || [];
        return (
          <div>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              {perms.length} permission(s)
            </span>
            {perms.length > 0 && (
              <div className="mt-1 text-xs text-gray-500 max-w-xs truncate">
                {perms.slice(0, 2).join(', ')}
                {perms.length > 2 && '...'}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Créé le',
      render: (role) => (
        <span className="text-sm text-gray-600">
          {role.created_at ? new Date(role.created_at).toLocaleDateString('fr-FR') : '-'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (role) => (
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setSelectedRole(role);
              setShowPermissionManager(true);
            }}
            className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center"
            title="Gérer les permissions"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
            </svg>
            Permissions
          </button>
          <button
            onClick={() => {
              setSelectedRole(role);
              setShowRoleForm(true);
            }}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            Modifier
          </button>
          <button
            onClick={() => handleDeleteRole(role.id)}
            className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            Supprimer
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8">
        {/* Header avec bouton d'ajout */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Gestion des rôles
              </h1>
              <p className="text-gray-600 text-lg">
                Gérez les rôles et leurs permissions
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedRole(null);
                setShowRoleForm(true);
              }}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium shadow-sm flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Nouveau rôle
            </button>
          </div>
        </div>

        {/* Cartes de statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total rôles</p>
                <p className="text-2xl font-bold text-gray-900">{roles.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Niveau moyen</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.round(roles.reduce((acc, r) => acc + (r.level || 0), 0) / (roles.length || 1))}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Avec permissions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {roles.filter(r => r.permissions && r.permissions.length > 0).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-5-5A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Permissions totales</p>
                <p className="text-2xl font-bold text-gray-900">
                  {roles.reduce((acc, role) => acc + (role.permissions?.length || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des rôles */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <DataTable data={roles} columns={columns} />
          </div>
        </div>
      </div>

      {/* Modal de création/modification de rôle */}
      {showRoleForm && (
        <RoleForm
          role={selectedRole}
          onClose={() => {
            setShowRoleForm(false);
            setSelectedRole(null);
          }}
          onSave={selectedRole ? handleUpdateRole : handleCreateRole}
        />
      )}

      {/* Modal de gestion des permissions */}
      {showPermissionManager && selectedRole && (
        <PermissionManager
          role={selectedRole}
          onClose={() => {
            setShowPermissionManager(false);
            setSelectedRole(null);
          }}
          onSave={handleUpdatePermissions}
        />
      )}
    </div>
  );
};

export default RolesPage;